// Firebase Authentication Service with University ID login support
import api from "./api";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updateProfile,
  onAuthStateChanged,
  getIdToken,
  GoogleAuthProvider,
  signInWithPopup,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  limit,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { auth, db, storage } from "./firebase";

class AuthService {
  // Check if user is authenticated
  getCurrentUser() {
    return auth.currentUser;
  }

  // Get current auth state as a Promise
  getCurrentUserPromise() {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        (user) => {
          unsubscribe();
          resolve(user);
        },
        reject
      );
    });
  }

  // Setup auth state listener
  onAuthStateChanged(callback) {
    return onAuthStateChanged(auth, callback);
  }

  // Get ID token (useful for API requests)
  async getIdToken(forceRefresh = false) {
    const user = this.getCurrentUser();
    if (!user) {
      console.log("No user found when requesting token");
      return null;
    }

    try {
      console.log("Getting ID token, force refresh:", forceRefresh);
      // Force refresh to ensure we get the latest token
      const token = await getIdToken(user, forceRefresh);
      console.log(
        "Token obtained successfully:",
        token ? "Valid token" : "No token"
      );
      return token;
    } catch (error) {
      console.error("Error getting ID token:", error);

      // If the first attempt failed and we didn't force refresh, try once more with force refresh
      if (!forceRefresh) {
        console.log("Retrying with force refresh...");
        try {
          // Wait briefly before retrying
          await new Promise((resolve) => setTimeout(resolve, 1000));
          return await this.getIdToken(true);
        } catch (retryError) {
          console.error("Error on retry getting ID token:", retryError);
          return null;
        }
      }

      return null;
    }
  }

  // Register a new user
  async register(email, password, userData) {
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      // Create user profile in Firestore
      await setDoc(doc(db, "users", user.uid), {
        ...userData,
        email,
        createdAt: new Date(),
      });

      // Update display name
      if (userData.firstName && userData.lastName) {
        await updateProfile(user, {
          displayName: `${userData.firstName} ${userData.lastName}`,
        });
      }

      return user;
    } catch (error) {
      throw error;
    }
  }

  // Login with email & password with retry mechanism
  async login(email, password, retryCount = 0) {
    const maxRetries = 3;
    const retryDelay = Math.pow(2, retryCount) * 1000; // Exponential backoff

    try {
      console.log(`🔐 Attempting Firebase Auth login... (attempt ${retryCount + 1}/${maxRetries + 1})`);

      // Clear any stale auth state before login
      if (auth.currentUser) {
        console.log("🧹 Clearing existing auth state...");
        await signOut(auth);
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      console.log("✅ Firebase Auth login successful");
      return userCredential.user;

    } catch (error) {
      console.error(`❌ Firebase Auth login failed (attempt ${retryCount + 1}):`, error.code, error.message);

      // Handle network errors with retry logic
      if (error.code === 'auth/network-request-failed' && retryCount < maxRetries) {
        console.log(`🔄 Network error detected. Retrying in ${retryDelay}ms... (${retryCount + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return this.login(email, password, retryCount + 1);
      }

      // Handle specific Firebase auth errors
      if (error.code === 'auth/network-request-failed') {
        throw new Error("Network connection failed after multiple attempts. Please check your internet connection and try again in a few moments.");
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error("Too many failed login attempts. Please wait a few minutes and try again.");
      } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        throw new Error("Invalid email or password. Please check your credentials and try again.");
      } else if (error.code === 'auth/invalid-email') {
        throw new Error("Please enter a valid email address.");
      } else if (error.code === 'auth/user-disabled') {
        throw new Error("This account has been disabled. Please contact support.");
      } else if (error.code === 'auth/invalid-credential') {
        throw new Error("Invalid login credentials. Please check your email and password.");
      }

      // Generic fallback for other errors
      throw new Error(`Login failed: ${error.message || 'Unknown error occurred'}`);
    }
  }

  // Login with University ID & password via backend lookup with retry mechanism
  async loginWithUniversityId(universityId, password, retryCount = 0) {
    const maxRetries = 3;
    const retryDelay = Math.pow(2, retryCount) * 1000; // Exponential backoff

    try {
      console.log(`🏫 Attempting University ID login... (attempt ${retryCount + 1}/${maxRetries + 1})`);
      console.log(`📡 API Request URL: ${process.env.REACT_APP_SERVER_URL}/api/v1/auth/university/${encodeURIComponent(universityId)}`);

      // Fetch associated email via backend API
      const response = await api.get(
        `/auth/university/${encodeURIComponent(universityId)}`
      );

      console.log("🔍 API Response:", response.status, response.data);

      const email = response.data.email;
      if (!email) {
        throw new Error(
          "Unable to retrieve email for provided University ID"
        );
      }

      console.log("📧 Found email for University ID, attempting Firebase Auth...");

      // Clear any stale auth state before login
      if (auth.currentUser) {
        console.log("🧹 Clearing existing auth state...");
        await signOut(auth);
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Perform Firebase Auth sign-in
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      console.log("✅ University ID login successful");
      return userCredential.user;
    } catch (error) {
      console.error(`❌ University ID login error (attempt ${retryCount + 1}):`, error);
      console.error("📊 Error details:", {
        code: error.code,
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          method: error.config?.method
        }
      });

      // Handle network errors with retry logic
      if ((error.code === 'auth/network-request-failed' ||
        error.code === 'NETWORK_ERROR' ||
        error.code === 'ERR_NETWORK' ||
        error.message?.includes('Network Error')) && retryCount < maxRetries) {
        console.log(`🔄 Network error detected. Retrying in ${retryDelay}ms... (${retryCount + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return this.loginWithUniversityId(universityId, password, retryCount + 1);
      }

      // Handle specific errors
      if (error.code === 'auth/network-request-failed') {
        throw new Error("Network connection failed after multiple attempts. Please check your internet connection and try again.");
      } else if (error.response?.status === 404) {
        throw new Error("University ID not found. Please check your ID and try again.");
      } else if (error.response?.status >= 500) {
        throw new Error("Server error. Please try again later.");
      } else if (error.code === 'auth/wrong-password') {
        throw new Error("Invalid password for this University ID.");
      } else if (error.code === 'auth/invalid-credential') {
        throw new Error("Invalid University ID or password.");
      } else if (error.code === 'auth/user-disabled') {
        throw new Error("This account has been disabled. Please contact support.");
      }

      // Handle API connection errors
      if (error.response?.status === 0 || error.message?.includes('Network Error')) {
        throw new Error("Unable to connect to authentication server. Please check your connection and try again.");
      }

      throw error;
    }
  }

  // Logout
  async logout() {
    await signOut(auth);
  }

  // Get user profile from Firestore
  async getUserProfile(uid) {
    try {
      // Get Firestore data
      const docRef = doc(db, "users", uid);
      const docSnap = await getDoc(docRef);
      console.log(
        `[AuthService] Fetching user profile for UID ${uid}:`,
        docSnap.exists() ? "Found in Firestore" : "Not found in Firestore"
      );

      // Get Firebase Auth token with claims - force refresh to ensure claims are current
      const currentUser = auth.currentUser;
      let customClaims = null;

      if (currentUser) {
        try {
          // Force a token refresh to ensure we get the latest claims
          await currentUser.getIdToken(true);
          const idTokenResult = await currentUser.getIdTokenResult();
          customClaims = idTokenResult.claims;
          console.log(
            "[AuthService] Firebase Auth custom claims:",
            customClaims
          );
        } catch (error) {
          console.error("[AuthService] Error fetching token claims:", error);
        }
      }

      // Prepare the user profile data
      let userData = null;
      let firestoreData = null;
      let userRole = null;

      if (docSnap.exists()) {
        firestoreData = docSnap.data();
        userRole =
          customClaims && customClaims.role
            ? customClaims.role
            : firestoreData.role || null;

        // Make sure we include both department and departmentId fields
        userData = {
          id: docSnap.id,
          ...firestoreData,
          role: userRole,
          // Ensure these fields are explicitly added even if null
          department: firestoreData.department || null,
          departmentId: firestoreData.departmentId || null,
        };

        // Log department information for debugging
        console.log(`[AuthService] User department data:`, {
          department: userData.department,
          departmentId: userData.departmentId,
        });

        // Self-healing: If Firestore is missing role but claims have it, update Firestore
        if (!firestoreData.role && customClaims && customClaims.role) {
          try {
            await updateDoc(docRef, { role: customClaims.role });
            console.warn(
              `[AuthService] Firestore user profile for UID ${uid} was missing role. Updated to:`,
              customClaims.role
            );
          } catch (e) {
            console.error(
              `[AuthService] Failed to update Firestore user role for UID ${uid}:`,
              e
            );
          }
        }
      } else if (customClaims) {
        userData = {
          id: uid,
          ...customClaims,
          role: customClaims.role || null,
          department: customClaims.department || null,
          departmentId: customClaims.departmentId || null,
        };
      }

      // Log if role is still missing
      if (userData && !userData.role) {
        console.warn(
          `[AuthService] Warning: User has no role defined in either claims or Firestore! UID: ${uid}`
        );
      }

      return userData;
    } catch (error) {
      console.error("[AuthService] Error fetching user profile:", error);
      return null;
    }
  }

  // Reset password
  async resetPassword(email) {
    await sendPasswordResetEmail(auth, email);
  }

  // Update user password
  async updatePassword(currentPassword, newPassword) {
    try {
      const user = this.getCurrentUser();
      if (!user) throw new Error("No user is logged in");

      // Re-authenticate user first
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );
      await reauthenticateWithCredential(user, credential);

      // Then update password
      await updatePassword(user, newPassword);
    } catch (error) {
      throw error;
    }
  }

  // Login with Google
  async loginWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if user profile exists in Firestore, if not create it
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (!userDoc.exists()) {
        const names = user.displayName ? user.displayName.split(" ") : ["", ""];
        const firstName = names[0] || "";
        const lastName = names.slice(1).join(" ") || "";

        await setDoc(doc(db, "users", user.uid), {
          email: user.email,
          firstName,
          lastName,
          photoURL: user.photoURL,
          createdAt: new Date(),
        });
      }

      return user;
    } catch (error) {
      throw error;
    }
  }

  // Check if MFA is enabled for the current user
  isMFAEnabled() {
    const user = this.getCurrentUser();
    if (!user) return false;

    const mfaUser = multiFactor(user);
    return mfaUser.enrolledFactors.length > 0;
  }

  // Get enrolled MFA factors for the current user
  getMFAFactors() {
    const user = this.getCurrentUser();
    if (!user) return [];

    const mfaUser = multiFactor(user);
    return mfaUser.enrolledFactors;
  }

  // Start MFA enrollment process (returns verification ID)
  async startMFAEnrollment(phoneNumber) {
    try {
      const user = this.getCurrentUser();
      if (!user) throw new Error("No user is logged in");

      const multiFactorSession = await multiFactor(user).getSession();

      // Send verification code to phone
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneNumber,
        multiFactorSession
      );

      return verificationId;
    } catch (error) {
      throw error;
    }
  }

  // Complete MFA enrollment with verification code
  async completeMFAEnrollment(
    verificationId,
    verificationCode,
    displayName = "My phone"
  ) {
    try {
      const user = this.getCurrentUser();
      if (!user) throw new Error("No user is logged in");

      // Create credential with verification code
      const phoneAuthCredential = PhoneAuthProvider.credential(
        verificationId,
        verificationCode
      );

      // Create multi-factor assertion
      const multiFactorAssertion =
        PhoneMultiFactorGenerator.assertion(phoneAuthCredential);

      // Enroll the phone as a second factor
      await multiFactor(user).enroll(multiFactorAssertion, displayName);

      return true;
    } catch (error) {
      throw error;
    }
  }

  // Remove MFA factor
  async unenrollMFAFactor(factorUid) {
    try {
      const user = this.getCurrentUser();
      if (!user) throw new Error("No user is logged in");

      await multiFactor(user).unenroll({ factorUid });
      return true;
    } catch (error) {
      throw error;
    }
  }

  // Upload user avatar
  async uploadAvatar(file) {
    try {
      const user = this.getCurrentUser();
      if (!user) throw new Error("No user is logged in");

      // Create a storage reference
      const avatarRef = ref(
        storage,
        `avatars/${user.uid}/${Date.now()}_${file.name}`
      );

      // Upload file
      await uploadBytes(avatarRef, file);

      // Get download URL
      const downloadURL = await getDownloadURL(avatarRef);

      // Update user profile in Firebase Auth
      await updateProfile(user, {
        photoURL: downloadURL,
      });

      // Update user profile in Firestore
      await updateDoc(doc(db, "users", user.uid), {
        photoURL: downloadURL,
      });

      return downloadURL;
    } catch (error) {
      throw error;
    }
  }

  // Delete user avatar
  async deleteAvatar() {
    try {
      const user = this.getCurrentUser();
      if (!user) throw new Error("No user is logged in");
      if (!user.photoURL) throw new Error("No avatar to delete");

      // Extract path from URL
      const path = decodeURIComponent(
        user.photoURL.split("/o/")[1].split("?")[0]
      );
      const avatarRef = ref(storage, path);

      // Delete file from storage
      await deleteObject(avatarRef);

      // Reset photoURL in Auth and Firestore
      await updateProfile(user, {
        photoURL: null,
      });

      await updateDoc(doc(db, "users", user.uid), {
        photoURL: null,
      });

      return true;
    } catch (error) {
      throw error;
    }
  }

  // Update user profile data (firstName, lastName, etc.)
  async updateUserProfile(profileData) {
    try {
      const user = this.getCurrentUser();
      if (!user) throw new Error("No user is logged in");

      // Update in Firestore
      await updateDoc(doc(db, "users", user.uid), profileData);

      // If name is being updated, also update displayName in Auth
      if (profileData.firstName || profileData.lastName) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        const userData = userDoc.data();

        const displayName = `${userData.firstName || ""} ${userData.lastName || ""
          }`.trim();
        await updateProfile(user, { displayName });
      }

      return true;
    } catch (error) {
      throw error;
    }
  }
}

export const authService = new AuthService();
